'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { Wrench, Eye, EyeOff, AlertCircle, ArrowLeft } from 'lucide-react';
import { firstAllowedAdminPath } from '@/lib/admin-module-access';
import { useBrand } from '@/components/providers/BrandProvider';

type FormValues = { email: string; password: string };

const DEMO_PASSWORD = 'demo1234';

// Greito prisijungimo mygtukų stilių paletė (taikoma pagal indeksą).
// Patys asmenys ateina iš prekės ženklo konfigūracijos (useBrand().quickLogin).
const QUICK_LOGIN_STYLES = [
  { badgeClass: 'bg-[#1e3a5f] text-white', buttonClass: 'border-slate-300 bg-slate-100 hover:bg-slate-200' },
  { badgeClass: 'bg-indigo-600 text-white', buttonClass: 'border-indigo-200 bg-indigo-50 hover:bg-indigo-100' },
  { badgeClass: 'bg-violet-600 text-white', buttonClass: 'border-violet-200 bg-violet-50 hover:bg-violet-100' },
  { badgeClass: 'bg-emerald-600 text-white', buttonClass: 'border-emerald-200 bg-emerald-50 hover:bg-emerald-100' },
];

const AUTH_ERROR_MESSAGES: Record<string, string> = {
  CredentialsSignin: 'Neteisingas el. paštas arba slaptažodis.',
  Configuration:
    'Autentifikacijos konfigūracija. Patikrinkite NEXTAUTH_URL (turi sutapti su naršyklės adresu).',
  AccessDenied: 'Prieiga uždrausta.',
  Default: 'Prisijungimas nepavyko. Bandykite dar kartą.',
};

export default function StaffLoginForm() {
  const router = useRouter();
  const brand = useBrand();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const code = searchParams.get('error');
    if (code) {
      setError(AUTH_ERROR_MESSAGES[code] ?? AUTH_ERROR_MESSAGES.Default);
    }
  }, [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const quickLogin = (email: string, password: string) => {
    void onSubmit({ email, password });
  };

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    setError(null);
    try {
      const res = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      });
      if (res?.error) {
        setError('Neteisingas el. paštas arba slaptažodis.');
        return;
      }
      const sessionRes = await fetch('/api/auth/session');
      const session = await sessionRes.json();
      const role = session?.user?.role;
      if (role === 'WORKER') {
        setError('Meistrai jungiasi per darbuotojo kodą (/login/meistrai).');
        return;
      }
      const callback = searchParams.get('callbackUrl');
      if (callback && callback.startsWith('/')) {
        router.push(callback);
        return;
      }
      if (role === 'ADMIN') {
        router.push(firstAllowedAdminPath(session?.user?.allowedModuleKeys));
        return;
      }
      router.push('/admin-dashboard');
    } catch {
      setError('Prisijungimo klaida. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <Link
          href="/login"
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Atgal
        </Link>

        <div className="text-center mb-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/api/logo" alt={brand.name} className="h-12 w-auto mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Vadyba</h1>
          <p className="text-sm text-slate-500 mt-1">Prisijunkite el. paštu ir slaptažodžiu</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="label-text">El. paštas</label>
            <input
              type="email"
              autoComplete="email"
              placeholder="pvz. vardas@imone.lt"
              className={`input-field ${errors.email ? 'border-red-400' : ''}`}
              {...register('email', { required: 'El. paštas yra privalomas' })}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <label className="label-text">Slaptažodis</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Jūsų slaptažodis"
                className={`input-field pr-10 ${errors.password ? 'border-red-400' : ''}`}
                {...register('password', { required: 'Slaptažodis yra privalomas' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#1e3a5f] text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-[#162d4a] active:scale-95 transition-all duration-150 disabled:opacity-60"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Jungiamasi...
              </>
            ) : (
              'Prisijungti'
            )}
          </button>

          <p className="text-center mt-4">
            <Link href="/forgot-password" className="text-sm text-[#1e3a5f] font-semibold hover:underline">
              Pamiršote slaptažodį?
            </Link>
          </p>
        </form>

        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5 space-y-3">
          <p className="text-[11px] font-semibold text-amber-700 uppercase tracking-wide">
            Greitas prisijungimas (testas)
          </p>
          <div className="flex flex-col gap-2">
            {brand.quickLogin.map((account, i) => {
              const style = QUICK_LOGIN_STYLES[i % QUICK_LOGIN_STYLES.length];
              return (
                <button
                  key={account.email}
                  type="button"
                  disabled={loading}
                  onClick={() => quickLogin(account.email, DEMO_PASSWORD)}
                  className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border active:scale-[0.98] transition-all disabled:opacity-50 text-left ${style.buttonClass}`}
                >
                  <div>
                    <p className="text-xs font-semibold text-slate-800">{account.name}</p>
                    <p className="text-[10px] text-slate-500">{account.email}</p>
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${style.badgeClass}`}
                  >
                    {account.badge}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
